import api from "../../../../../../services/api";

import React, { useCallback, useRef, useState } from "react";
import { useForm } from "react-hook-form";

import "./styles.css";

function normalizeCep(value) {
  return value.replace(/\D/g, "");
}

export default function UserRegistrationUpdateForm() {
  const [addressEnabled, setAddressEnabled] = useState(false);
  const [loading, setLoading] = useState(false);
  const cepLookup = useRef(null);
  const cepLookupValue = useRef("");
  const submissionInFlight = useRef(false);
  const {
    register,
    handleSubmit,
    setFocus,
    setValue,
    formState: { errors },
  } = useForm();

  const clearAddress = useCallback(() => {
    ["place", "neighbourhood", "city", "uf"].forEach((field) => {
      setValue(field, "", { shouldDirty: true, shouldValidate: true });
    });
    cepLookup.current = null;
    cepLookupValue.current = "";
    setAddressEnabled(false);
  }, [setValue]);

  const resetCepAndAddress = useCallback(() => {
    clearAddress();
    setValue("cep", "", { shouldDirty: true, shouldValidate: true });
  }, [clearAddress, setValue]);

  const lookupCep = useCallback(
    async (cepValue) => {
      const normalizedCep = normalizeCep(cepValue);

      if (normalizedCep.length !== 8) {
        return false;
      }

      if (
        cepLookup.current &&
        cepLookupValue.current === normalizedCep
      ) {
        return cepLookup.current;
      }

      if (cepLookupValue.current === normalizedCep) {
        return true;
      }

      cepLookupValue.current = normalizedCep;
      const request = fetch(
        `https://viacep.com.br/ws/${normalizedCep}/json/`
      ).then((res) => res.json());
      cepLookup.current = request;

      try {
        const apiCepInfo = await request;

        if (cepLookupValue.current !== normalizedCep) {
          return false;
        }

        if (apiCepInfo.erro) {
          resetCepAndAddress();
          alert("CEP inválido. Por favor, digite um CEP válido");
          return false;
        }

        setAddressEnabled(true);
        [
          ["place", apiCepInfo.logradouro],
          ["neighbourhood", apiCepInfo.bairro],
          ["uf", apiCepInfo.uf],
          ["city", apiCepInfo.localidade],
        ].forEach(([field, fieldValue]) => {
          setValue(field, fieldValue || "", {
            shouldDirty: true,
            shouldValidate: true,
          });
        });

        return true;
      } catch (err) {
        if (cepLookupValue.current !== normalizedCep) {
          return false;
        }

        resetCepAndAddress();
        alert("Ocorreu um erro ao consultar o CEP.");
        return false;
      } finally {
        if (cepLookup.current === request) {
          cepLookup.current = null;
        }
      }
    },
    [resetCepAndAddress, setValue]
  );

  function handleCepChange(event) {
    const normalizedCep = normalizeCep(event.target.value);

    cepField.onChange(event);

    if (normalizedCep.length === 8) {
      if (cepLookupValue.current !== normalizedCep) {
        clearAddress();
      }
      void lookupCep(normalizedCep);
    } else {
      clearAddress();
    }
  }

  function handleCepBlur(event) {
    const normalizedCep = normalizeCep(event.target.value);

    cepField.onBlur(event);

    if (normalizedCep === "") {
      resetCepAndAddress();
    } else if (normalizedCep.length !== 8) {
      resetCepAndAddress();
      alert("Formato de CEP inválido.");
    } else {
      void lookupCep(normalizedCep);
    }
  }

  async function onSubmit(data) {
    if (submissionInFlight.current) {
      return;
    }

    submissionInFlight.current = true;
    setLoading(true);

    try {
      const result = await api.post("/form/atualizacao-cadastral", data);
      if (!alert(result.data.message)) {
        window.scroll(0, 0);
        window.location.reload();
      }
    } catch (err) {
      console.log(err);
      if (!alert("Ocorreu um erro! Tente novamente mais tarde.")) {
        window.scroll(0, 0);
        window.location.reload();
      }
    } finally {
      submissionInFlight.current = false;
      setLoading(false);
    }
  }

  async function onFormSubmit(event) {
    if (submissionInFlight.current) {
      event.preventDefault();
      return;
    }

    event.preventDefault();
    const form = event.currentTarget;
    const currentCep = form.elements.cep.value;
    const normalizedCep = normalizeCep(currentCep);

    setValue("cep", normalizedCep, {
      shouldDirty: true,
      shouldValidate: true,
    });

    if (normalizedCep.length === 8) {
      if (cepLookupValue.current !== normalizedCep) {
        clearAddress();
      }
      await lookupCep(normalizedCep);
    }

    await handleSubmit(onSubmit, (validationErrors) => {
      const firstInvalidField = Object.keys(validationErrors)[0];

      if (firstInvalidField) {
        setFocus(firstInvalidField);
      }
    })(event);
  }

  const cepField = register("cep", {
    required: true,
    validate: (value) =>
      /^[0-9]{8}$/.test(normalizeCep(value)) ||
      "Informe um CEP válido.",
  });

  return (
    <div className="denuncia__form">
      <form onSubmit={onFormSubmit}>
        <div className="form__title">
          <p>Atualização Cadastral</p>
        </div>

        <div className="form__options">
          <div className="option__column">
            <p>Selecione sua categoria</p>
            <ul>
              <li>
                <input
                  {...register("profile", { required: true })}
                  type="radio"
                  value="fisioterapia"
                />
                Fisioterapia
              </li>
              <li>
                <input
                  {...register("profile", { required: true })}
                  type="radio"
                  value="terapia ocupacional"
                />
                Terapia Ocupacional
              </li>
            </ul>
          </div>
        </div>

        <div className="form__divisor"></div>

        <div className="form__group">
          <input
            {...register("register", { required: true })}
            type="text"
            placeholder="Número de registro"
          />
          <input
            {...register("name", { required: true })}
            type="text"
            placeholder="Nome completo"
          />
          <div className="form__group--half">
            <input
              {...register("email", { required: true })}
              type="email"
              placeholder="Endereço de E-Mail"
            />
            <input
              {...register("cel", { required: true })}
              type="tel"
              placeholder="Telefone"
            />
          </div>
        </div>

        <div className="form__divisor"></div>

        <div className="form__group">
          <label htmlFor="cep">CEP:</label>
          <input
            id="cep"
            {...cepField}
            type="text"
            inputMode="numeric"
            autoComplete="postal-code"
            placeholder="CEP"
            onChange={handleCepChange}
            onBlur={handleCepBlur}
          />
          <label htmlFor="place">Endereço:</label>
          <input
            id="place"
            {...register("place", { required: true })}
            type="text"
            autoComplete="address-line1"
            placeholder="Logradouro"
            disabled={!addressEnabled}
          />
          <div className="form__group--half">
            <input
              id="number"
              {...register("number", { required: true })}
              type="text"
              autoComplete="address-line2"
              placeholder="Número"
              disabled={!addressEnabled}
            />
            <input
              id="complement"
              {...register("complement")}
              type="text"
              autoComplete="address-line2"
              placeholder="Complemento"
              disabled={!addressEnabled}
            />
          </div>
          <input
            id="neighbourhood"
            {...register("neighbourhood", { required: true })}
            type="text"
            autoComplete="address-level3"
            placeholder="Bairro"
            disabled={!addressEnabled}
          />
          <div className="form__group--half">
            <input
              id="uf"
              {...register("uf", { required: true })}
              type="text"
              defaultValue=""
              autoComplete="address-level1"
              placeholder="Estado (UF)"
              disabled={!addressEnabled}
            />
            <input
              id="city"
              {...register("city", { required: true })}
              type="text"
              defaultValue=""
              autoComplete="address-level2"
              placeholder="Cidade"
              disabled={!addressEnabled}
            />
          </div>
          <textarea
            {...register("message", { required: true })}
            id="message"
            cols="30"
            rows="10"
            placeholder="Comentário"
          ></textarea>
          <div className="user_authorization">
            <input
              id="userAuthorization"
              {...register("userAuthorization", { required: true })}
              type="checkbox"
              value="authorized"
              aria-invalid={errors.userAuthorization ? "true" : "false"}
              aria-describedby={
                errors.userAuthorization
                  ? "userAuthorization-error"
                  : undefined
              }
            />
            <label htmlFor="userAuthorization">
              Autorizo o envio de informações para meu e-mail.
            </label>
            {errors.userAuthorization && (
              <span
                id="userAuthorization-error"
                className="form__error"
                role="alert"
              >
                Você precisa autorizar o envio de informações.
              </span>
            )}
          </div>
          <input
            id="submit"
            className={`button${loading ? " btn-disabled" : ""}`}
            type="submit"
            value="Enviar"
            disabled={loading}
          />
        </div>
      </form>
    </div>
  );
}
